import type { Cell, Worksheet } from 'exceljs';

export const MAX_PREVIEW_ROWS = 500;
export const MAX_PREVIEW_COLUMNS = 50;

export type SheetGrid = {
  name: string;
  rows: string[][];
  isTruncated: boolean;
};

export type WorkbookSheets = {
  names: string[];
  gridAt: (sheetIndex: number) => SheetGrid;
};

const formatDate = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const dateText = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  const hasTime =
    hours !== 0 ||
    minutes !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCMilliseconds() !== 0;

  return hasTime ? `${dateText} ${pad(hours)}:${pad(minutes)}` : dateText;
};

const cellText = (cell: Cell): string => {
  if (cell.isMerged && cell.master !== cell) return '';
  if (cell.formula) {
    const result = cell.result;
    if (result === undefined || result === null) return `=${cell.formula}`;
    if (result instanceof Date) return formatDate(result);
    return cell.text;
  }
  if (cell.value instanceof Date) return formatDate(cell.value);
  return cell.text;
};

const toSheetGrid = (worksheet: Worksheet): SheetGrid => {
  const rowCount = Math.min(worksheet.rowCount, MAX_PREVIEW_ROWS);
  const columnCount = Math.min(worksheet.columnCount, MAX_PREVIEW_COLUMNS);

  const rows = Array.from({ length: rowCount }, (_, rowIndex) => {
    const row = worksheet.getRow(rowIndex + 1);
    return Array.from({ length: columnCount }, (_, columnIndex) =>
      cellText(row.getCell(columnIndex + 1))
    );
  });

  return {
    name: worksheet.name,
    rows,
    isTruncated:
      worksheet.rowCount > rowCount || worksheet.columnCount > columnCount,
  };
};

export const readWorkbookSheets = async (
  bytes: ArrayBuffer
): Promise<WorkbookSheets> => {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  await workbook.xlsx.load(bytes);
  const worksheets = workbook.worksheets;
  if (worksheets.length === 0) throw new Error('Workbook has no sheets');

  const builtGrids = new Map<number, SheetGrid>();

  return {
    names: worksheets.map((worksheet) => worksheet.name),
    gridAt: (sheetIndex) => {
      const builtGrid = builtGrids.get(sheetIndex);
      if (builtGrid) return builtGrid;

      const grid = toSheetGrid(worksheets[sheetIndex]);
      builtGrids.set(sheetIndex, grid);
      return grid;
    },
  };
};
